import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { SongData, SongState, WordState, ShowWordMode, HideWordMode, WindowMessage } from '../types'
import { SongManager } from '../services/SongManager'
import { PRNG } from '../utils/prng'
import type { MainWindowPlatform } from '../platform/types'
import { preloadGospelTermImages } from '../utils/imagePreloader'

/**
 * Pure function that applies word visibility based on percentage and shuffled indices.
 * Uses deterministic selection to ensure consistent word hiding patterns.
 *
 * @param song - The song state to modify
 * @param percentage - Percentage of words to hide (0-100)
 * @param indices - Pre-shuffled array of word IDs for deterministic selection
 * @returns New song state with updated word visibility
 */
const applyVisibilityToSong = (song: SongState, percentage: number, indices: string[]): SongState => {
	const newPages = song.pages.map(page => {
		const pageWords = page.flat().filter(w => !w.isPlain)
		const pageWordIds = new Set(pageWords.map(w => w.id))

		const shuffledIndicesForPage = indices.filter(id => pageWordIds.has(id))
		const totalWordsToShow = Math.round(shuffledIndicesForPage.length * (1 - percentage / 100))
		const visibleWordIdsOnPage = new Set(shuffledIndicesForPage.slice(0, totalWordsToShow))

		return page.map(line =>
			line.map(word => ({
				...word,
				isVisible: visibleWordIdsOnPage.has(word.id) || !!word.isPlain,
			})),
		)
	})

	return {
		...song,
		pages: newPages,
	}
}

/**
 * Pure function that generates a shuffled array of word IDs using deterministic PRNG.
 * Uses Fisher-Yates shuffle algorithm for uniform distribution.
 *
 * @param song - The song state containing words to shuffle
 * @param prng - PRNG instance for deterministic randomization
 * @param seed - Seed value for reproducible shuffle results
 * @returns Array of shuffled word IDs
 */
const shuffleWordsForSong = (song: SongState, prng: PRNG, seed: number): string[] => {
	prng.setSeed(seed)
	const allWords = song.pages.flat(2).filter(w => !w.isPlain)
	const indices = allWords.map(w => w.id)

	// Fisher-Yates shuffle
	for (let i = indices.length - 1; i > 0; i--) {
		const j = Math.floor(prng.next() * (i + 1));
		[indices[i], indices[j]] = [indices[j], indices[i]]
	}
	return indices
}

/**
 * Zustand store interface for managing song state, display settings, and window communication.
 * This store handles all state that needs to be synchronized between the main window and display window.
 *
 * @interface SongStore
 */
interface SongStore {
	// Song State
	/** Current loaded song with word visibility states, null if no song loaded */
	songState: SongState | null
	/** Percentage of words to hide (0-100) */
	hiddenPercentage: number
	/** Deterministically shuffled array of word IDs for consistent visibility patterns */
	shuffledWordIndices: string[]
	/** ID of the last processed song to track state changes */
	lastProcessedSongId: string | null

	// Display State (synced to display window)
	/** Current page index being displayed (0-based) */
	currentPageIndex: number
	/** Font size for display window (18-180px) */
	fontSize: number
	/** Mode for showing words ('both', 'visible', 'hidden') */
	showWordMode: ShowWordMode
	/** Mode for hiding words ('hide', 'first-letter') */
	hideWordMode: HideWordMode
	/** Whether the display window is currently open */
	isDisplayWindowOpen: boolean

	// Song Actions
	/**
	 * Load a new song and initialize its state
	 * @param songData - Song data to load
	 * @param initialPercentage - Initial percentage of words to hide (default: 0)
	 */
	loadSong: (songData: SongData, initialPercentage?: number) => void

	/**
	 * Toggle visibility of a specific word
	 * @param wordId - Unique identifier of the word to toggle
	 * @returns The toggled word state, or undefined if word not found
	 */
	toggleWord: (wordId: string) => WordState | undefined

	/**
	 * Update the percentage of words to hide and recalculate visibility
	 * @param percentage - New percentage value (0-100), will be clamped to valid range
	 */
	updateHiddenPercentage: (percentage: number) => void

	/** Generate new shuffle pattern for word visibility while maintaining current percentage */
	shuffleWords: () => void

	// Display Actions
	/**
	 * Set the current page index
	 * @param index - Page index to display (0-based)
	 */
	setCurrentPageIndex: (index: number) => void

	/**
	 * Set the font size for display
	 * @param size - Font size in pixels (18-180)
	 */
	setFontSize: (size: number) => void

	/**
	 * Set the word display mode
	 * @param mode - Display mode for words
	 */
	setShowWordMode: (mode: ShowWordMode) => void

	/**
	 * Set the word hiding mode
	 * @param mode - Mode for hiding words
	 */
	setHideWordMode: (mode: HideWordMode) => void

	// Navigation Actions (with bounds checking)
	/** Navigate to next page with bounds checking */
	nextPage: () => void
	/** Navigate to previous page with bounds checking */
	prevPage: () => void

	// Window Management
	/**
	 * Open the display window using platform-specific implementation
	 * @param platform - Platform adapter for window management
	 * @param title - window title
	 */
	openDisplayWindow: (platform: MainWindowPlatform, title?: string) => Promise<void>

	/**
	 * Close the display window using platform-specific implementation
	 * @param platform - Platform adapter for window management
	 */
	closeDisplayWindow: (platform: MainWindowPlatform) => Promise<void>

	/**
	 * Set the display window open state (internal use)
	 * @param isOpen - Whether the window is open
	 */
	setIsDisplayWindowOpen: (isOpen: boolean) => void

	// Communication (syncs current state to display)
	/**
	 * Synchronize current store state to the display window
	 * @param platform - Platform adapter for message sending
	 */
	syncToDisplay: (platform: MainWindowPlatform) => void

	// Message Handlers (updates local state and syncs)
	/**
	 * Handle incoming messages from the display window
	 * @param message - Window message with type and payload
	 */
	handleDisplayMessage: (message: WindowMessage) => void
}

/**
 * Zustand store for managing song state, display settings, and window communication.
 * This is the main store that handles all state synchronization between the control window
 * and display window in the Memtastic application.
 *
 * Features:
 * - Song loading and word visibility management
 * - Deterministic PRNG-based word hiding with percentage control
 * - Display window lifecycle management
 * - Real-time state synchronization between windows
 * - Page navigation with bounds checking
 * - Font size and display mode controls
 * - Message handling for bi-directional communication
 * - Devtools integration for debugging
 *
 * @returns SongStore hook for accessing song state and actions
 */
export const useSongStore = create<SongStore>()(
	devtools(
		(set, get) => {
			// Create PRNG instance that will be reused
			const prng = new PRNG()

			return {
				// Initial Song State
				songState: null,
				hiddenPercentage: 0,
				shuffledWordIndices: [],
				lastProcessedSongId: null,

				// Initial Display State
				currentPageIndex: 0,
				fontSize: 48,
				showWordMode: 'both',
				hideWordMode: 'hide',
				isDisplayWindowOpen: false,

				// Song Actions
				loadSong: (songData: SongData, initialPercentage: number = 0) => {
					const newSongState = SongManager.createSongState(songData)

					// Initialize with fresh shuffle and specified percentage
					const newSeed = Date.now()
					const indices = shuffleWordsForSong(newSongState, prng, newSeed)

					// Apply specified percentage to new song
					const songWithVisibility = applyVisibilityToSong(newSongState, initialPercentage, indices)

					// Preload gospel term images for smooth page transitions
					preloadGospelTermImages(songData.lyrics)

					set({
						songState: songWithVisibility,
						lastProcessedSongId: songData.id,
						shuffledWordIndices: indices,
						hiddenPercentage: initialPercentage,
						currentPageIndex: 0, // Reset page when loading new song
					})
				},

				toggleWord: (wordId: string) => {
					let toggledWord: WordState | undefined

					set((state) => {
						if (!state.songState) return state

						const newPages = state.songState.pages.map(page =>
							page.map(line =>
								line.map(word => {
									if (word.id === wordId) {
										toggledWord = { ...word, isVisible: !word.isVisible }
										return toggledWord
									}
									return word
								}),
							),
						)

						return {
							...state,
							songState: {
								...state.songState,
								pages: newPages,
							},
						}
					})

					return toggledWord
				},

				updateHiddenPercentage: (newPercentage: number) => {
					const clampedPercentage = Math.max(0, Math.min(100, newPercentage))

					set((state) => {
						if (!state.songState) return { ...state, hiddenPercentage: clampedPercentage }

						const songWithVisibility = applyVisibilityToSong(
							state.songState,
							clampedPercentage,
							state.shuffledWordIndices,
						)

						return {
							...state,
							songState: songWithVisibility,
							hiddenPercentage: clampedPercentage,
						}
					})
				},

				shuffleWords: () => {
					set((state) => {
						if (!state.songState) return state

						// Generate new shuffle
						const newSeed = Date.now()
						const indices = shuffleWordsForSong(state.songState, prng, newSeed)

						// Apply current percentage with new shuffle
						const songWithVisibility = applyVisibilityToSong(
							state.songState,
							state.hiddenPercentage,
							indices,
						)

						return {
							...state,
							songState: songWithVisibility,
							shuffledWordIndices: indices,
						}
					})
				},

				// Display Actions
				setCurrentPageIndex: (index: number) => {
					set({ currentPageIndex: index })
				},

				setFontSize: (size: number) => {
					set({ fontSize: size })
				},

				setShowWordMode: (mode: ShowWordMode) => {
					set({ showWordMode: mode })
				},

				setHideWordMode: (mode: HideWordMode) => {
					set({ hideWordMode: mode })
				},

				// Navigation Actions (with bounds checking)
				nextPage: () => {
					set((state) => {
						if (!state.songState) return state

						const maxPageIndex = state.songState.pages.length - 1
						const newIndex = Math.min(state.currentPageIndex + 1, maxPageIndex)

						return {
							...state,
							currentPageIndex: newIndex,
						}
					})
				},

				prevPage: () => {
					set((state) => {
						const newIndex = Math.max(state.currentPageIndex - 1, 0)

						return {
							...state,
							currentPageIndex: newIndex,
						}
					})
				},

				// Window Management
				openDisplayWindow: async (platform: MainWindowPlatform, title?: string) => {
					try {
						await platform.openDisplayWindow(title)
						set({ isDisplayWindowOpen: true })

						// Listen for state changes from platform
						platform.onDisplayWindowStateChange!((isOpen) => {
							set({ isDisplayWindowOpen: isOpen })
						})
					} catch (error) {
						console.error('Failed to open display window:', error)
					}
				},

				closeDisplayWindow: async (platform: MainWindowPlatform) => {
					try {
						await platform.closeDisplayWindow()
						set({ isDisplayWindowOpen: false })
					} catch (error) {
						console.error('Failed to close display window:', (error as Error).message)
					}
				},

				setIsDisplayWindowOpen: (isOpen: boolean) => {
					set({ isDisplayWindowOpen: isOpen })
				},

				// Communication (syncs current state to display)
				syncToDisplay: (platform: MainWindowPlatform) => {
					const state = get()
					if (!state.songState || !state.isDisplayWindowOpen) return

					try {
						// Send song update
						platform.sendToDisplay({
							type: 'UPDATE_SONG',
							payload: {
								songData: state.songState,
							},
						})

						// Send settings update
						platform.sendToDisplay({
							type: 'UPDATE_SETTINGS',
							payload: {
								fontSize: state.fontSize,
								showWordMode: state.showWordMode,
								hideWordMode: state.hideWordMode,
								currentPageIndex: state.currentPageIndex,
							},
						})
					} catch (error) {
						console.error('Failed to sync to display:', error)
					}
				},

				// Message Handlers (updates local state and syncs)
				handleDisplayMessage: (message: WindowMessage) => {
					const { type, payload } = message

					switch (type) {
						case 'TOGGLE_WORD_FROM_DISPLAY':
							if (payload.wordId) {
								get().toggleWord(payload.wordId)
							}
							break

						case 'NAVIGATE_PAGE':
							console.log('handling NAVIGATE_PAGE message', payload.direction)
							if (payload.direction === 'next') {
								get().nextPage()
							} else if (payload.direction === 'prev') {
								get().prevPage()
							}
							break

						case 'UPDATE_HIDDEN_PERCENTAGE':
							if (payload.direction === 'increase') {
								const currentPercentage = get().hiddenPercentage
								get().updateHiddenPercentage(Math.min(100, currentPercentage + 5))
							} else if (payload.direction === 'decrease') {
								const currentPercentage = get().hiddenPercentage
								get().updateHiddenPercentage(Math.max(0, currentPercentage - 5))
							}
							break

						case 'TOGGLE_HIDDEN_PERCENTAGE':
						{
							const currentPercentage = get().hiddenPercentage
							get().updateHiddenPercentage(currentPercentage === 0 ? 100 : 0)
							break
						}

						case 'DISPLAY_READY':
							// Note: DISPLAY_READY sync is handled by the component's useEffect
							// that listens for display window state changes
							break
						default:
							console.warn('Unhandled display message type:', type)
					}
				},
			}
		},
		{ name: 'song-store' },
	),
)
